import type { FlagKey } from '@doneverse/shared'
import {
  Activity,
  Award,
  BarChart3,
  BookOpen,
  Bot,
  Building2,
  CalendarClock,
  CheckSquare,
  ClipboardCheck,
  ClipboardList,
  FileText,
  Files,
  Flag,
  FolderOpen,
  Gauge,
  GraduationCap,
  Handshake,
  History,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  MapPin,
  MessageCircle,
  MessageSquareText,
  MessagesSquare,
  MonitorPlay,
  Network,
  NotebookPen,
  PackageCheck,
  Phone,
  Rocket,
  Search,
  Sparkles,
  Tags,
  TriangleAlert,
  UploadCloud,
  UserPlus,
  UserRound,
  Users,
  UsersRound,
  Video,
} from 'lucide-react'
import type { SidebarNavItem } from './SectionSidebar'
import iconBusinessBrief from '@/assets/brand/icon-business-brief.png'
import iconDoingPlaybooks from '@/assets/brand/icon-doing-playbooks.png'
import iconDoingTeams from '@/assets/brand/icon-doing-teams.png'
import iconLiveZoom from '@/assets/brand/icon-live-zoom.png'
import iconReferFriend from '@/assets/brand/icon-refer-friend.png'
import iconRoadmapBlueprint from '@/assets/brand/icon-roadmap-blueprint.png'
import iconStartHere from '@/assets/brand/icon-start-here.svg'
import { isAllowedForRestrictedDoer } from '@/lib/doerAccess'
import { TOUR_ANCHORS } from '@/lib/productTour'
import { matchesPathPrefix } from '@/lib/routes'

// External destinations for the Refer-a-Friend and Live-Zoom nav tabs.
// Same links surfaced in the Founder's Lounge action cards (see
// apps/api/scripts/learnworlds-founders-lounge-import.ts).
const REFER_A_FRIEND_URL = 'https://go.doneverse.com/refer-a-friend/'
const LIVE_ZOOM_URL = 'https://us02web.zoom.us/j/86772973357'

// Rail sections. Each value mirrors one icon in the rail (IconRailLayout) —
// clicking a rail icon lands on a route in that section, and the sidebar then
// shows only that section's items. Founders and doers both navigate via
// sectioned rails; the sections a role actually uses are role-gated on the rail
// icon (IconRailLayout), so the union of every role's sections lives here.
//   founder → roadmap | onboarding | coaching-community | training-tools
//   doer    → roadmap | doing-teams | coaching-community
//   admin   → admin-platform | admin-people | admin-content | admin-coaching
export type RailSection =
  | 'roadmap'
  | 'onboarding'
  | 'coaching-community'
  | 'training-tools'
  | 'doing-teams'
  | 'admin-platform'
  | 'admin-people'
  | 'admin-content'
  | 'admin-coaching'

export interface NavItemConfig extends SidebarNavItem {
  roles?: string[]
  excludeRoles?: string[]
  flag?: FlagKey
  // Which rail section this item belongs to. Items without a section only
  // appear for roles whose rail has no sub-sections (e.g. doing_team, intake).
  section?: RailSection
}

export const panelNavItems: NavItemConfig[] = [
  {
    to: '/start-here',
    label: 'Start Here',
    icon: MapPin,
    panelImage: iconStartHere,
    roles: ['founder'],
    section: 'roadmap',
    end: true,
  },
  {
    to: '/doer-home',
    label: 'Start Here',
    icon: MapPin,
    panelImage: iconStartHere,
    roles: ['doer'],
    section: 'roadmap',
    end: true,
  },
  {
    to: '/doing-blueprint',
    label: 'Doing Blueprint',
    icon: LayoutDashboard,
    panelImage: iconRoadmapBlueprint,
    roles: ['founder', 'doer'],
    section: 'roadmap',
    tourId: TOUR_ANCHORS.sidebarBlueprint,
  },
  {
    to: '/playbooks',
    label: 'Doing Playbooks',
    icon: BookOpen,
    panelImage: iconDoingPlaybooks,
    excludeRoles: ['doing_team', 'admin', 'intake_team', 'dsl'],
    section: 'roadmap',
    tourId: TOUR_ANCHORS.sidebarPlaybooks,
  },
  {
    to: '/tasks',
    label: 'Tasks',
    icon: CheckSquare,
    roles: ['founder', 'doer'],
    section: 'roadmap',
    tourId: TOUR_ANCHORS.sidebarTasks,
  },
  {
    to: '/teams',
    label: 'Doing Teams',
    icon: Handshake,
    panelImage: iconDoingTeams,
    roles: ['doer'],
    section: 'doing-teams',
    end: true,
  },
  {
    // The single AI coaching route: shows pre-session prep until the doer has an
    // in-progress session, then the coaching chat.
    to: '/ai-coaching',
    label: 'AI Coaching',
    icon: MessageCircle,
    roles: ['doer'],
    flag: 'pre-session-prep',
    section: 'coaching-community',
    end: true,
  },
  {
    // The doer's coaching history, directly under AI Coaching (ClickUp
    // 86d3g824r) — the same destination the home tile's "View past sessions"
    // link points at, promoted to the sidebar so it is reachable from anywhere
    // in the coaching section rather than only from /doer-home.
    //
    // Deliberately not `end`: the per-session detail lives under
    // /coaching-sessions/$sessionId and should keep this tab highlighted. It
    // also gives that path a section, so the rail/sidebar stay on Coaching
    // instead of falling back to the roadmap list.
    to: '/coaching-sessions',
    label: 'View past sessions',
    icon: History,
    roles: ['doer'],
    flag: 'pre-session-prep',
    section: 'coaching-community',
  },
  {
    to: '/business-brief',
    label: 'Business Brief',
    icon: FileText,
    panelImage: iconBusinessBrief,
    excludeRoles: ['doing_team', 'admin', 'intake_team', 'dsl'],
    section: 'onboarding',
    end: true,
  },
  {
    // The launch checklist — what the founder has agreed with their Doer, and
    // the Doer's read of it before day one. Same audience as the Business Brief
    // above and for the same reason. Ungated — `founder-onboarding` is per-user,
    // and gating on it would hide the page from the doer, who never carries
    // their founder's flag.
    //
    // NOT onboarding step 10 any more: Launch Day is (below). The checklist
    // survives on its own terms — the notes are useful to the Doer whether or
    // not anything gates on them — it just no longer decides when a founder
    // finishes onboarding (ClickUp 86d40tuc8).
    to: '/launch-checklist',
    label: 'Launch Checklist',
    icon: ClipboardCheck,
    excludeRoles: ['doing_team', 'admin', 'intake_team', 'dsl'],
    section: 'onboarding',
    end: true,
  },
  {
    // Onboarding step 10 — the countdown to the founder's first day with their
    // Doer, and the button that finishes onboarding (ClickUp 86d40tuc8).
    //
    // Founder-only, unlike its neighbours in this section: a doer has no launch
    // day of their own to count down to. Which is also what lets it be flagged —
    // behind `founder-onboarding`, the flag the route guard itself enforces for
    // `/launch-day`, so the tab cannot offer a page the router would bounce them
    // straight off.
    to: '/launch-day',
    label: 'Launch Day',
    icon: Rocket,
    roles: ['founder'],
    flag: 'founder-onboarding',
    section: 'onboarding',
    end: true,
  },
  {
    // Onboarding step 9. One route, two labels: the founder is meeting someone,
    // the doer is editing themselves, and a single shared label would be wrong
    // for one of them. Same shape as /start-here vs /doer-home above.
    //
    // Placed after the Launch Checklist so the onboarding rail still opens on
    // the Business Brief (`sectionHomeRoute` takes the section's first item).
    to: '/doer-profile',
    label: 'Your Doer',
    icon: UserRound,
    roles: ['founder'],
    section: 'onboarding',
    end: true,
  },
  {
    to: '/doer-profile',
    label: 'Your Profile',
    icon: UserRound,
    roles: ['doer'],
    section: 'onboarding',
    end: true,
  },
  {
    to: '/kick-off-call',
    label: 'Kick-off Call',
    icon: Phone,
    roles: ['founder'],
    flag: 'kick-off-call',
    section: 'onboarding',
    end: true,
  },
  {
    // Onboarding step 6's own surface, and the founder's to re-watch long after
    // the step has closed — which is why it earns a nav entry rather than only
    // the panel's CTA.
    //
    // Behind `founder-onboarding` because that is the flag the route guard
    // itself enforces for `/foundations` (useFeatureFlagRouteGuard), so the tab
    // cannot offer a page the router would bounce them straight off.
    to: '/foundations',
    label: 'Founder Foundations',
    icon: GraduationCap,
    roles: ['founder'],
    flag: 'founder-onboarding',
    section: 'onboarding',
    end: true,
  },
  {
    to: '/coaching-calls',
    label: 'Coaching Calls',
    icon: Phone,
    roles: ['founder'],
    flag: 'coaching-call-recordings',
    section: 'coaching-community',
  },
  {
    // Founder-only. Doers had this too until ClickUp 86d3ve54a: their community
    // is the per-cohort one in LearnWorlds, which the platform is not replacing,
    // so a second half-populated space for them was worse than none. Nothing
    // else changes — the groups routes, the API and every existing membership
    // stay exactly as they are, and admins reach the same view via their own
    // entry in the admin-people section below.
    to: '/groups',
    label: 'Community',
    icon: UsersRound,
    roles: ['founder'],
    flag: 'groups',
    section: 'coaching-community',
  },
  {
    to: '/masterclass-vault',
    label: 'Masterclass Vault',
    icon: MonitorPlay,
    roles: ['founder'],
    flag: 'masterclass-vault',
    section: 'training-tools',
  },
  {
    to: '/doers-university',
    label: 'Doer University',
    icon: GraduationCap,
    roles: ['founder'],
    flag: 'doers-university',
    section: 'training-tools',
  },
  {
    to: '/tech-stack-finder',
    label: 'Tech Stack Finder',
    icon: Layers,
    roles: ['founder'],
    flag: 'tech-stack-finder',
    section: 'training-tools',
    end: true,
  },
  {
    to: '/founders-lounge',
    label: "Founder's Lounge",
    icon: LifeBuoy,
    roles: ['founder'],
    flag: 'founders-lounge',
    section: 'training-tools',
  },
  {
    href: REFER_A_FRIEND_URL,
    label: 'Refer a Friend',
    icon: UserPlus,
    panelImage: iconReferFriend,
    roles: ['founder'],
    flag: 'founders-lounge',
    section: 'training-tools',
  },
  {
    href: LIVE_ZOOM_URL,
    label: 'Get Live Support',
    icon: Video,
    panelImage: iconLiveZoom,
    roles: ['founder'],
    flag: 'founders-lounge',
    section: 'training-tools',
  },
  {
    // Independent "submit your work" form (ClickUp 86d3g825y). Same
    // `pre-session-prep` flag as the rest of the doer coaching loop. Lives in the
    // roadmap section because /submit-work resolves to the roadmap rail.
    to: '/submit-work',
    label: 'Submit work',
    icon: UploadCloud,
    roles: ['doer'],
    flag: 'pre-session-prep',
    section: 'roadmap',
    end: true,
  },
  {
    to: '/files',
    label: 'Files',
    icon: Files,
    roles: ['founder', 'doer'],
    section: 'roadmap',
    tourId: TOUR_ANCHORS.sidebarFiles,
  },
  {
    // Loyalty points carry their own flag rather than riding the coaching
    // rollout: coaching close is what writes to the ledger, but the display is
    // Chunk 1 only and not ready to launch alongside it. Hidden from a
    // founder-less doer like every other surface (see lib/doerAccess).
    to: '/loyalty',
    label: 'Loyalty points',
    icon: Award,
    roles: ['doer'],
    flag: 'loyalty-points',
    section: 'roadmap',
    end: true,
  },
  {
    to: '/workflows',
    label: 'Workflows',
    icon: Sparkles,
    roles: ['founder', 'doer'],
    flag: 'workflows',
    section: 'roadmap',
  },
  {
    to: '/projects',
    label: 'Projects',
    icon: FolderOpen,
    roles: ['doing_team'],
    end: true,
  },
  {
    to: '/intake',
    label: 'Intake Queue',
    icon: ClipboardList,
    roles: ['intake_team'],
    end: true,
  },
  {
    // Escalation officers ride a flat rail (no sub-sections), so this item has
    // no `section` — like Projects/Intake Queue above.
    to: '/escalations',
    label: 'Escalation Console',
    icon: TriangleAlert,
    roles: ['escalation_officer'],
  },
  {
    // A DSL's whole nav: the Activity Log and Summary, scoped to their team by the API.
    // Flat like the Escalation Console; admins reach the same page through the
    // sectioned item below.
    to: '/admin/activity-log',
    label: 'Activity Log',
    icon: Activity,
    roles: ['dsl'],
  },
  // No flag: the API scopes a DSL's summary to their team and the page hides Export.
  { to: '/admin/activity-summary', label: 'Activity Summary', icon: Gauge, roles: ['dsl'] },
  // ─── Admin ────────────────────────────────────────────────────────
  // Admin navigates a sectioned rail like founders/doers. Each item is gated to
  // the admin role and assigned to one of the four admin sections; the sidebar
  // shows only the active section's items.
  { to: '/admin/stats', label: 'Stats', icon: BarChart3, roles: ['admin'], section: 'admin-platform' },
  {
    to: '/admin/activity-log',
    label: 'Activity Log',
    icon: Activity,
    roles: ['admin'],
    section: 'admin-platform',
  },
  {
    to: '/admin/activity-search',
    label: 'Activity Search',
    icon: Search,
    roles: ['admin'],
    section: 'admin-platform',
    // Per-user (ClickUp 86d4cqpjz); the API enforces the same flag.
    flag: 'activity-search',
  },
  {
    to: '/admin/activity-summary',
    label: 'Activity Summary',
    icon: Gauge,
    roles: ['admin'],
    section: 'admin-platform',
    // Per-user; the API enforces the same flag (ClickUp 14zf25zn1pp).
    flag: 'activity-summary',
  },
  {
    to: '/admin/activity-classifications',
    label: 'Classifications',
    icon: Tags,
    roles: ['admin'],
    section: 'admin-platform',
  },
  {
    to: '/admin/agent-releases',
    label: 'Agent releases',
    icon: PackageCheck,
    roles: ['admin'],
    section: 'admin-platform',
  },
  {
    to: '/admin/feature-flags',
    label: 'Feature flags',
    icon: Flag,
    roles: ['admin'],
    section: 'admin-platform',
  },
  {
    to: '/admin/feedback',
    label: 'Feedback',
    icon: MessageSquareText,
    roles: ['admin'],
    section: 'admin-platform',
  },
  { to: '/admin/users', label: 'Users', icon: Users, roles: ['admin'], section: 'admin-people' },
  {
    to: '/admin/categories',
    label: 'Categories',
    icon: Building2,
    roles: ['admin'],
    section: 'admin-people',
  },
  { to: '/admin/teams', label: 'Teams', icon: UsersRound, roles: ['admin'], section: 'admin-people' },
  {
    to: '/admin/doer-groups',
    label: 'Doer Groups',
    icon: Network,
    roles: ['admin'],
    section: 'admin-people',
  },
  {
    // Independent group chats, whose membership is role-neutral. Messages has
    // no management affordances for anyone, so this page — not /messages — is
    // the only place a chat is created or its membership edited, even for an
    // admin who is a member of it.
    to: '/admin/group-chats',
    label: 'Group Chats',
    icon: MessageCircle,
    roles: ['admin'],
    section: 'admin-people',
  },
  {
    // Admins browse the unified groups view at /groups (same as founders/doers);
    // resolveRailSection maps it back to admin-people for the admin rail.
    to: '/groups',
    label: 'Groups',
    icon: MessagesSquare,
    roles: ['admin'],
    section: 'admin-people',
  },
  { to: '/admin/playbooks', label: 'Playbooks', icon: BookOpen, roles: ['admin'], section: 'admin-content' },
  {
    to: '/admin/masterclass-vault',
    label: 'Masterclass Vault',
    icon: GraduationCap,
    roles: ['admin'],
    section: 'admin-content',
  },
  { to: '/admin/dsl-agent', label: 'DSL Agent', icon: Bot, roles: ['admin'], section: 'admin-content' },
  {
    to: '/admin/coaching-sessions',
    label: 'Coaching Sessions',
    icon: NotebookPen,
    roles: ['admin'],
    section: 'admin-coaching',
  },
  {
    to: '/admin/escalations',
    label: 'Escalations',
    icon: TriangleAlert,
    roles: ['admin'],
    section: 'admin-coaching',
  },
  {
    to: '/cx-risk',
    label: 'CX Risk Dashboard',
    icon: Gauge,
    roles: ['admin', 'escalation_officer'],
    section: 'admin-coaching',
  },
  {
    to: '/weekly-review-queue',
    label: 'Weekly Reviews',
    icon: ClipboardCheck,
    roles: ['admin', 'escalation_officer'],
    section: 'admin-coaching',
  },
  {
    to: '/admin/coaching-calls',
    label: 'Coaching Calls',
    icon: CalendarClock,
    roles: ['admin'],
    section: 'admin-coaching',
    end: true,
  },
  {
    to: '/admin/coaching-calls/replays',
    label: 'Call Replays',
    icon: Video,
    roles: ['admin'],
    section: 'admin-coaching',
  },
]

function matchesRole(item: NavItemConfig, role: string | undefined): boolean {
  if (item.roles && !item.roles.includes(role || '')) return false
  if (item.excludeRoles && item.excludeRoles.includes(role || '')) return false
  return true
}

// Which section owns a path, derived from the registry above: the first item
// whose `to` is a prefix of the path, role-filtered — so the shared /groups
// path resolves to coaching-community for founders/doers but admin-people for
// admins. Flags are deliberately ignored: a flag-gated route you are on still
// belongs to its section. Returns null when no section owns the path (flat-rail
// routes like /messages, standalone pages like /notifications).
export function resolveRailSection(pathname: string, role?: string): RailSection | null {
  const owner = panelNavItems.find(
    (item) => item.to && item.section && matchesRole(item, role) && matchesPathPrefix(pathname, item.to),
  )
  return owner?.section ?? null
}

export function isNavItemVisible(
  item: NavItemConfig,
  role: string | undefined,
  hasFlag: (flag: FlagKey) => boolean,
  // A doer with no founder is restricted to Home + Settings, so only items
  // pointing at an allowed surface stay in the menu (see lib/doerAccess).
  restrictedDoer = false,
): boolean {
  if (!matchesRole(item, role)) return false
  if (item.flag && !hasFlag(item.flag)) return false
  if (restrictedDoer && !(item.to && isAllowedForRestrictedDoer(item.to))) return false
  return true
}

export function countSectionItems(
  section: RailSection,
  role: string | undefined,
  hasFlag: (flag: FlagKey) => boolean,
  restrictedDoer = false,
): number {
  return panelNavItems.filter(
    (item) => item.section === section && isNavItemVisible(item, role, hasFlag, restrictedDoer),
  ).length
}

// The route a rail icon lands on when entering its section: the first visible
// sidebar item for that section + role. This keeps the rail in lock-step with
// the sidebar's order (roadmap lands on "Start Here", its first item — which is
// /start-here for founders but /doer-home for doers) instead of hardcoding a
// route on the rail that can silently drift out of sync. Returns undefined when
// the section has no navigable (`to`) item visible to the role.
export function sectionHomeRoute(
  section: RailSection,
  role: string | undefined,
  hasFlag: (flag: FlagKey) => boolean,
  restrictedDoer = false,
): string | undefined {
  const first = panelNavItems.find(
    (item) => item.section === section && item.to && isNavItemVisible(item, role, hasFlag, restrictedDoer),
  )
  return first?.to
}
